import { useState } from "react";
import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import { SERVICES, waLink, EMAIL, mailtoQuoteLink } from "../../lib/site";
import { WhatsAppIcon } from "./bits";

export const QuoteSection = () => {
    const [form, setForm] = useState({
        servico: "Cargas Fracionadas",
        origem: "",
        destino: "",
        peso: "",
    });

    const update = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    const submit = (e) => {
        e.preventDefault();
        window.open(waLink(buildQuoteMessage(form)), "_blank", "noopener");
    };

    const inputClass =
        "w-full rounded-lg border border-white/10 bg-ink/70 px-4 py-3 text-sm text-mist placeholder:text-mute/50 outline-none transition-colors duration-300 focus:border-magenta";

    return (
        <section
            id="cotacao"
            data-testid="quote-cta-section"
            className="relative overflow-hidden border-y border-white/5 bg-surface py-24 lg:py-32"
            style={{
                background:
                    "radial-gradient(60% 80% at 85% 15%, rgba(176,38,214,0.16), transparent 60%), #130D18",
            }}
        >
            <div className="checkers absolute right-0 top-0 h-24 w-72 opacity-10 [mask-image:linear-gradient(to_left,black,transparent)]" />

            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 lg:grid-cols-2 lg:gap-20 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-tangerine">
                        // Cotação rápida
                    </span>
                    <h2 className="mt-4 font-display text-4xl font-extrabold uppercase italic leading-[0.95] text-mist sm:text-6xl">
                        Peça sua cotação{" "}
                        <span className="text-magenta">agora</span>
                    </h2>
                    <p className="mt-6 max-w-lg text-base leading-relaxed text-mute">
                        Sem formulário longo, sem espera. Preencha ao lado e
                        receba a cotação no WhatsApp ou por e-mail, com
                        atendimento rápido e personalizado.
                    </p>
                    <div className="mt-8 flex flex-col items-start gap-3">
                        <a
                            href={waLink(
                                "Olá! Vim pelo site da Borges Cargas e quero uma cotação de frete.",
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="quote-section-direct-whatsapp-link"
                            className="inline-flex items-center gap-2 font-mono text-sm tracking-wider text-zap transition-opacity hover:opacity-80"
                        >
                            <WhatsAppIcon className="h-5 w-5" />
                            +55 85 98109-0149
                        </a>
                        <a
                            href={`mailto:${EMAIL}`}
                            data-testid="quote-section-direct-email-link"
                            className="inline-flex items-center gap-2 font-mono text-sm tracking-wider text-tangerine transition-opacity hover:opacity-80"
                        >
                            <Mail className="h-5 w-5" />
                            {EMAIL}
                        </a>
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
                    className="rounded-2xl border border-white/10 bg-card/90 p-6 shadow-[0_0_80px_rgba(176,38,214,0.12)] md:p-8"
                    data-testid="quote-form"
                >
                    <div className="space-y-5">
                        <div>
                            <label
                                htmlFor="servico"
                                className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mute"
                            >
                                Serviço
                            </label>
                            <select
                                id="servico"
                                data-testid="quote-service-select"
                                value={form.servico}
                                onChange={update("servico")}
                                className={inputClass}
                            >
                                {SERVICES.map((s) => (
                                    <option key={s.id} value={s.title} className="bg-card">
                                        {s.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="origem"
                                    className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mute"
                                >
                                    Origem
                                </label>
                                <input
                                    id="origem"
                                    data-testid="quote-origin-input"
                                    required
                                    value={form.origem}
                                    onChange={update("origem")}
                                    placeholder="Cidade - UF"
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="destino"
                                    className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mute"
                                >
                                    Destino
                                </label>
                                <input
                                    id="destino"
                                    data-testid="quote-destination-input"
                                    required
                                    value={form.destino}
                                    onChange={update("destino")}
                                    placeholder="Cidade - UF"
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <div>
                            <label
                                htmlFor="peso"
                                className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mute"
                            >
                                Peso aproximado <span className="normal-case text-mute/50">(opcional)</span>
                            </label>
                            <input
                                id="peso"
                                data-testid="quote-weight-input"
                                value={form.peso}
                                onChange={update("peso")}
                                placeholder="Ex: 500 kg"
                                className={inputClass}
                            />
                        </div>

                        <button
                            type="submit"
                            data-testid="quote-submit-whatsapp-button"
                            className="skew-cta flex h-14 w-full items-center justify-center bg-magenta text-base font-semibold text-white shadow-[0_0_40px_rgba(176,38,214,0.4)] transition-all duration-300 hover:bg-magenta-deep"
                        >
                            <span className="unskew flex items-center gap-3">
                                <WhatsAppIcon className="h-5 w-5" />
                                Receber cotação no WhatsApp
                            </span>
                        </button>

                        <a
                            href={mailtoQuoteLink(form)}
                            data-testid="quote-submit-email-button"
                            className="skew-cta flex h-14 w-full items-center justify-center border border-magenta/60 text-base font-semibold text-mist transition-colors duration-300 hover:bg-magenta/10"
                        >
                            <span className="unskew flex items-center gap-3">
                                <Mail className="h-5 w-5 text-magenta" />
                                Receber cotação por e-mail
                            </span>
                        </a>
                    </div>
                </motion.form>
            </div>
        </section>
    );
};
